import { Divider } from '../../components/Divider';
import { Dropdown } from '../../components/Dropdown';
import { FormCard } from '../../components/FormCard';
import { FormField } from '../../components/FormField';
import { TextInput } from '../../components/FormField/FormField.styles';
import { RadioGroup } from '../../components/RadioGroup';
import { SectionHeader } from '../../components/SectionHeader';
import { SelectablePill } from '../../components/SelectablePill';
import {
  FormSection,
  GenderLabel,
  GenderSection,
  Panel,
  PillGroup,
} from './GuestDetailsPanel.styles';
import type { GuestDetailsPanelProps } from './GuestDetailsPanel.types';

const DEFAULT_TITLES = ['Mr', 'Mrs', 'Ms', 'Miss'];
const DEFAULT_GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
];

export function GuestDetailsPanel({
  guestNumber,
  subtitle,
  existingGuests = [],
  selectedGuestId,
  disabledGuestIds = [],
  onExistingGuestSelect,
  values,
  onChange,
  titleOptions = DEFAULT_TITLES,
  genderOptions = DEFAULT_GENDERS,
  dividerText,
  errors,
  labels,
}: GuestDetailsPanelProps) {
  const resolvedSubtitle = subtitle ?? labels?.subtitle ?? "Tell us who's coming.";
  const resolvedDividerText = dividerText ?? labels?.dividerText ?? 'or';
  const titleLabel = labels?.titleLabel ?? 'Title';
  const titlePlaceholder = labels?.titlePlaceholder ?? 'Select';
  const firstNameLabel = labels?.firstNameLabel ?? 'First name';
  const lastNameLabel = labels?.lastNameLabel ?? 'Last name';
  const genderLabel = labels?.genderLabel ?? 'Gender';
  const guestTitle = labels?.guestTitleFormatter
    ? labels.guestTitleFormatter(guestNumber)
    : `Guest ${guestNumber}.`;

  const titleDropdownOptions = titleOptions.map((t) => ({ value: t, label: t }));

  const handleTitleChange = (val: string) => {
    onChange('title', val);
    const gender =
      genderOptions.length === 1 ? genderOptions[0].value : val === 'Mr' ? 'male' : 'female';
    onChange('gender', gender);
  };

  const defaultGender = genderOptions.length === 1 ? genderOptions[0].value : '';

  const handleGuestPillClick = (guestId: string) => {
    if (selectedGuestId === guestId) {
      onExistingGuestSelect(undefined);
      onChange('title', '');
      onChange('firstName', '');
      onChange('lastName', '');
      onChange('gender', defaultGender);
    } else {
      const guest = existingGuests.find((g) => g.id === guestId);
      if (guest) {
        onExistingGuestSelect(guestId);
        onChange('title', guest.title);
        onChange('firstName', guest.firstName);
        onChange('lastName', guest.lastName);
        onChange('gender', guest.gender);
      }
    }
  };

  return (
    <Panel>
      <SectionHeader title={guestTitle} subtitle={resolvedSubtitle} />

      {existingGuests.length > 0 && (
        <>
          <PillGroup>
            {existingGuests.map((guest) => (
              <SelectablePill
                key={guest.id}
                label={`${guest.title} ${guest.name}`}
                selected={selectedGuestId === guest.id}
                disabled={disabledGuestIds.includes(guest.id)}
                onClick={() => handleGuestPillClick(guest.id)}
              />
            ))}
          </PillGroup>
          <Divider text={resolvedDividerText} />
        </>
      )}

      <FormSection>
        <FormCard>
          <FormField label={titleLabel} required error={errors?.title}>
            <Dropdown
              options={titleDropdownOptions}
              value={values.title}
              placeholder={titlePlaceholder}
              onChange={(val) => handleTitleChange(val)}
              aria-label={titleLabel}
            />
          </FormField>
          <FormField label={firstNameLabel} required error={errors?.firstName}>
            <TextInput
              name={guestNumber === 1 ? 'firstName' : `guest-${guestNumber}-firstName`}
              value={values.firstName}
              onChange={(e) => onChange('firstName', e.target.value)}
              autoComplete={guestNumber === 1 ? 'given-name' : 'off'}
              aria-invalid={!!errors?.firstName || undefined}
            />
          </FormField>
          <FormField label={lastNameLabel} required error={errors?.lastName}>
            <TextInput
              name={guestNumber === 1 ? 'lastName' : `guest-${guestNumber}-lastName`}
              value={values.lastName}
              onChange={(e) => onChange('lastName', e.target.value)}
              autoComplete={guestNumber === 1 ? 'family-name' : 'off'}
              aria-invalid={!!errors?.lastName || undefined}
            />
          </FormField>
        </FormCard>
      </FormSection>

      <GenderSection>
        <GenderLabel>{genderLabel}</GenderLabel>
        <RadioGroup
          name={`guest-${guestNumber}-gender`}
          options={genderOptions}
          value={values.gender}
          onChange={(val) => onChange('gender', val)}
        />
      </GenderSection>
    </Panel>
  );
}
